import { defineStore } from 'pinia'
import { ethers } from 'ethers'
import { getMember, getDirectMembers, getIdoOverview, getLeaderboard, getTeamPerformance } from '../utils/api'
import { t } from '../i18n'

const CONTRACT_ADDRESS = process.env.VUE_APP_CONTRACT_ADDRESS || ''
const CHAIN_ID = Number(process.env.VUE_APP_CHAIN_ID || 56)
const SALE_ENDED = false
const ABI = [
  'function bind(address referrer)',
  'function ido() payable',
  'function isBound(address user) view returns (bool)',
  'function getUser(address user) view returns (tuple(address referrer, bool isBinded, uint256 deposits, uint256 directPerformance))',
]
const emptyMember = () => ({ performance: '0', directPerformance: '0', directCount: 0 })
const emptyOverview = () => ({ totalRaised: '0', totalRaiseTarget: '0', remainingSeats: 0, participantCount: 0, progress: 0, endAt: null, contractAddress: CONTRACT_ADDRESS, explorerUrl: process.env.VUE_APP_CHAIN_ID === '97' ? 'https://testnet.bscscan.com' : 'https://bscscan.com', auditStatus: '' })

export const useIdoStore = defineStore('ido', {
  state: () => ({
    walletAddress: '',
    pendingWalletAddress: '',
    provider: null,
    signer: null,
    member: emptyMember(),
    teamPerformance: null,
    teamPerformanceRequest: 0,
    directMembers: [],
    teamLoading: false,
    teamError: false,
    directTotal: 0,
    directPage: 1,
    directPageSize: 10,
    txPending: false,
    purchaseStatus: 'idle',
    bindingPending: false,
    referrer: '',
    isBound: null,
    referrerConfirmed: false,
    walletBalance: '--',
    gasEstimate: '--',
    overview: emptyOverview(),
    leaderboard: [],
    leaderboardTotal: 0,
    leaderboardPage: 1,
    leaderboardPageSize: 10,
    leaderboardLoading: false,
    leaderboardError: false,
    leaderboardRequest: 0,
  }),
  actions: {
    captureReferrer() {
      const value = new URLSearchParams(window.location.search).get('ref')
      if (value && ethers.utils.isAddress(value)) {
        this.referrer = ethers.utils.getAddress(value)
        this.referrerConfirmed = false
        window.localStorage.setItem('dk_referrer', this.referrer)
      } else {
        const saved = window.localStorage.getItem('dk_referrer')
        if (saved && ethers.utils.isAddress(saved)) this.referrer = saved
      }
    },
    setReferrer(value) {
      const candidate = String(value || '').trim()
      if (!ethers.utils.isAddress(candidate)) throw new Error(t('error.invalidAddress'))
      const normalized = ethers.utils.getAddress(candidate)
      const currentAddress = this.walletAddress || this.pendingWalletAddress
      if (currentAddress && normalized.toLowerCase() === currentAddress.toLowerCase()) throw new Error(t('error.selfReferrer'))
      this.referrer = normalized
      this.referrerConfirmed = true
      window.localStorage.setItem('dk_referrer', normalized)
      return normalized
    },
    async connect() {
      if (!window.ethereum) throw new Error(t('error.needWeb3'))
      this.provider = new ethers.providers.Web3Provider(window.ethereum, 'any')
      const accounts = await this.provider.send('eth_requestAccounts', [])
      if (!accounts.length) throw new Error(t('error.noAccount'))
      const network = await this.provider.getNetwork()
      if (CHAIN_ID && network.chainId !== CHAIN_ID) throw new Error(t('error.wrongNetwork', { chainId: CHAIN_ID }))
      this.signer = this.provider.getSigner()
      const address = ethers.utils.getAddress(accounts[0])
      this.bindWalletEvents()
      const bound = await this.checkBinding(address)
      if (bound === null) throw new Error(t('error.bindStatus'))
      if (!bound) {
        this.walletAddress = ''
        this.resetLeaderboard()
        this.pendingWalletAddress = address
        this.member = emptyMember()
        this.directMembers = []
        this.purchaseStatus = 'idle'
        return { requiresBinding: true }
      }
      this.activateWallet(address)
      await Promise.all([this.loadDashboard(), this.loadPublicData(), this.loadWalletMetrics()])
      return { requiresBinding: false }
    },
    async restore() {
      if (!window.ethereum) return
      if (window.localStorage.getItem('dk_wallet_disconnected') === 'true') return
      this.provider = new ethers.providers.Web3Provider(window.ethereum, 'any')
      const accounts = await this.provider.listAccounts()
      if (!accounts.length) return
      const network = await this.provider.getNetwork()
      if (CHAIN_ID && network.chainId !== CHAIN_ID) return
      this.signer = this.provider.getSigner()
      const address = ethers.utils.getAddress(accounts[0])
      this.bindWalletEvents()
      const bound = await this.checkBinding(address)
      if (!bound) {
        this.cancelPendingConnection()
        return
      }
      this.activateWallet(address)
      await Promise.all([this.loadDashboard(), this.loadPublicData(), this.loadWalletMetrics()])
    },
    bindWalletEvents() {
      if (!window.ethereum || window.ethereum.__dkBound) return
      window.ethereum.__dkBound = true
      window.ethereum.on('accountsChanged', async accounts => {
        if (!accounts.length) {
          this.clearWalletState()
          return
        }
        if (window.localStorage.getItem('dk_wallet_disconnected') === 'true') return
        this.signer = this.provider.getSigner()
        const address = ethers.utils.getAddress(accounts[0])
        const bound = await this.checkBinding(address)
        if (!bound) {
          this.walletAddress = ''
          this.resetLeaderboard()
          this.pendingWalletAddress = address
          this.member = emptyMember()
          this.directMembers = []
          this.purchaseStatus = 'idle'
          return
        }
        this.activateWallet(address)
        await Promise.all([this.loadDashboard(), this.loadWalletMetrics()])
      })
      window.ethereum.on('chainChanged', () => window.location.reload())
    },
    async checkBinding(address = this.walletAddress || this.pendingWalletAddress) {
      this.isBound = null
      if (!this.signer || !address || !CONTRACT_ADDRESS || !ethers.utils.isAddress(CONTRACT_ADDRESS)) return null
      try {
        const contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, this.signer)
        this.isBound = await contract.isBound(address)
        if (this.isBound) this.referrerConfirmed = true
        return this.isBound
      } catch (_) {
        this.isBound = null
        return null
      }
    },
    activateWallet(address) {
      this.resetLeaderboard()
      this.directMembers = []
      this.directTotal = 0
      this.directPage = 1
      this.teamError = false
      this.walletAddress = ethers.utils.getAddress(address)
      this.pendingWalletAddress = ''
      this.isBound = true
      this.purchaseStatus = 'checking'
      this.referrerConfirmed = true
      window.localStorage.removeItem('dk_wallet_disconnected')
    },
    clearWalletState() {
      this.resetLeaderboard()
      this.walletAddress = ''
      this.pendingWalletAddress = ''
      this.provider = null
      this.signer = null
      this.member = emptyMember()
      this.directMembers = []
      this.directTotal = 0
      this.directPage = 1
      this.teamLoading = false
      this.teamError = false
      this.purchaseStatus = 'idle'
      this.walletBalance = '--'
      this.gasEstimate = '--'
      this.isBound = null
      this.referrerConfirmed = false
      this.bindingPending = false
    },
    cancelPendingConnection() {
      window.localStorage.setItem('dk_wallet_disconnected', 'true')
      this.clearWalletState()
    },
    async disconnect() {
      if (window.ethereum && window.ethereum.request) {
        try {
          await window.ethereum.request({ method: 'wallet_revokePermissions', params: [{ eth_accounts: {} }] })
        } catch (_) { /* not supported by every injected wallet */ }
      }
      window.localStorage.setItem('dk_wallet_disconnected', 'true')
      this.clearWalletState()
    },
    async bindReferral() {
      if (!this.signer || !this.pendingWalletAddress) throw new Error(t('error.reconnect'))
      if (!this.referrerConfirmed || !this.referrer) throw new Error(t('error.confirmReferrer'))
      if (!CONTRACT_ADDRESS || !ethers.utils.isAddress(CONTRACT_ADDRESS)) throw new Error(t('error.noContract'))
      const pendingAddress = this.pendingWalletAddress
      if (this.referrer.toLowerCase() === pendingAddress.toLowerCase()) throw new Error(t('error.selfReferrer'))
      const contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, this.signer)
      this.bindingPending = true
      try {
        const alreadyBound = await contract.isBound(pendingAddress)
        if (!alreadyBound) {
          const transaction = await contract.bind(this.referrer)
          await transaction.wait(1)
        }
        this.activateWallet(pendingAddress)
        await Promise.all([this.loadDashboard(), this.loadWalletMetrics()])
        return true
      } finally { this.bindingPending = false }
    },
    async loadDashboard() {
      if (!this.walletAddress) return
      const address = this.walletAddress
      await Promise.all([
        getMember(address).then(member => {
          if (this.walletAddress === address) this.member = member || emptyMember()
        }),
        this.loadDirectMembers(1),
        this.loadLeaderboard(1),
        this.loadTeamPerformance(),
      ])
    },
    async loadTeamPerformance() {
      const address = this.walletAddress
      if (!address) return
      const request = ++this.teamPerformanceRequest
      this.teamPerformance = null
      try {
        const total = await getTeamPerformance(address)
        if (this.walletAddress === address && this.teamPerformanceRequest === request) this.teamPerformance = total
      } catch (_) {
        if (this.walletAddress === address && this.teamPerformanceRequest === request) this.teamPerformance = null
      }
    },
    async loadPublicData() {
      try {
        const overview = await getIdoOverview()
        this.overview = overview || emptyOverview()
      } catch (_) {
        this.overview = emptyOverview()
      }
    },
    async loadDirectMembers(page = 1) {
      const address = this.walletAddress
      if (!address) return
      this.teamLoading = true
      this.teamError = false
      try {
        const data = await getDirectMembers(address, { page, pageSize: this.directPageSize })
        if (this.walletAddress !== address) return
        this.directMembers = data.list
        this.directTotal = data.total
        this.directPage = data.page
      } catch (error) {
        if (this.walletAddress === address) this.teamError = error.message
      } finally {
        if (this.walletAddress === address) this.teamLoading = false
      }
    },
    resetLeaderboard() {
      this.teamPerformance = null
      this.teamPerformanceRequest += 1
      this.leaderboard = []
      this.leaderboardTotal = 0
      this.leaderboardPage = 1
      this.leaderboardLoading = false
      this.leaderboardError = false
      this.leaderboardRequest += 1
    },
    async loadLeaderboard(page = 1) {
      const address = this.walletAddress
      if (!address) return
      if (this.leaderboardLoading) return
      const request = ++this.leaderboardRequest
      this.leaderboardLoading = true
      this.leaderboardError = false
      try {
        const data = await getLeaderboard(address, { page, pageSize: this.leaderboardPageSize })
        if (this.walletAddress !== address || this.leaderboardRequest !== request) return
        this.leaderboard = data.list
        this.leaderboardTotal = data.total
        this.leaderboardPage = data.page
      } catch (error) {
        if (this.walletAddress === address && this.leaderboardRequest === request) this.leaderboardError = error.message
      } finally {
        if (this.walletAddress === address && this.leaderboardRequest === request) this.leaderboardLoading = false
      }
    },
    async loadWalletMetrics() {
      if (!this.provider || !this.walletAddress) return
      try {
        const balance = await this.provider.getBalance(this.walletAddress)
        this.walletBalance = Number(ethers.utils.formatEther(balance)).toFixed(4)
      } catch (_) { this.walletBalance = '--' }
      if (!CONTRACT_ADDRESS || !ethers.utils.isAddress(CONTRACT_ADDRESS) || !this.signer) {
        this.purchaseStatus = 'error'
        return
      }
      this.purchaseStatus = 'checking'
      try {
        const contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, this.signer)
        const user = await contract.getUser(this.walletAddress)
        const bound = user.isBinded
        if (!bound) {
          this.isBound = false
          this.purchaseStatus = 'error'
          return
        }
        if (user.deposits.gt(0)) {
          this.purchaseStatus = 'purchased'
          this.gasEstimate = '--'
          return
        }
        this.purchaseStatus = 'eligible'
        const [gas, gasPrice] = await Promise.all([
          contract.estimateGas.ido({ value: ethers.utils.parseEther('0.3') }),
          this.provider.getGasPrice()
        ])
        this.gasEstimate = Number(ethers.utils.formatEther(gas.mul(gasPrice))).toFixed(5)
      } catch (_) {
        this.purchaseStatus = 'error'
        this.gasEstimate = '--'
      }
    },
    async buy(amount) {
      if (SALE_ENDED) throw new Error(t('error.saleEnded'))
      if (!this.signer || !this.walletAddress || this.isBound !== true) throw new Error(t('error.bindFirst'))
      if (!CONTRACT_ADDRESS || !ethers.utils.isAddress(CONTRACT_ADDRESS)) throw new Error(t('error.noContract'))
      const contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, this.signer)
      this.txPending = true
      this.purchaseStatus = 'checking'
      try {
        let user
        try {
          user = await contract.getUser(this.walletAddress)
        } catch (_) {
          this.purchaseStatus = 'error'
          throw new Error(t('error.purchaseStatus'))
        }
        if (!user.isBinded) {
          this.purchaseStatus = 'error'
          throw new Error(t('error.notBound'))
        }
        if (user.deposits.gt(0)) {
          this.purchaseStatus = 'purchased'
          throw new Error(t('error.alreadyPurchased'))
        }
        this.purchaseStatus = 'eligible'
        const transaction = await contract.ido({ value: ethers.utils.parseEther(amount) })
        await transaction.wait(1)
        this.purchaseStatus = 'purchased'
        await Promise.all([this.loadDashboard(), this.loadPublicData(), this.loadWalletMetrics()])
        return transaction.hash
      } finally { this.txPending = false }
    }
  }
})
